import type {
  AnnouncementPriority,
  AnnouncementVariant,
} from '@tour-kit/announcements';
import type { FrequencyRule } from '@tour-kit/core';

import type { Dictionary } from '@/core/providers/dictionary-provider';

export type AnnouncementKind = 'ann.release' | 'ann.feature' | 'ann.ops';

/**
 * Code-defined announcement catalog. Copy lives in `guidance.announcements.*`
 * under the same stable `id`. Do not rename IDs without a progress migration.
 *
 * Agents: when shipping a worth-announcing customer-app feature, add an enabled
 * entry here + de/en copy in the same change (see docs/guidance/04-announcements.md).
 * Keep empty rather than placeholders.
 */
export type GuidanceAnnouncementDef = {
  id: string;
  kind: AnnouncementKind;
  variant: AnnouncementVariant;
  /** When false, excluded from auto-show and What’s new. */
  enabled: boolean;
  frequency: FrequencyRule;
  priority?: AnnouncementPriority;
  /** ISO date for What’s new archive ordering. */
  publishedAt?: string;
  /** Shell path for primary CTA deep-link (feature / release). */
  ctaPath?: string;
};

export type AnnouncementCopy = {
  title: string;
  body: string;
  cta?: string;
};

/** Prod catalog — add enabled entries only when shipping a real announcement. */
export const ANNOUNCEMENT_CATALOG: readonly GuidanceAnnouncementDef[] = [];

function isAnnouncementCopy(value: unknown): value is AnnouncementCopy {
  if (!value || typeof value !== 'object') return false;
  const record = value as Record<string, unknown>;
  return typeof record.title === 'string' && typeof record.body === 'string';
}

/** Resolve `guidance.announcements.<id>` entry copy. */
export function announcementCopyFor(
  dict: Dictionary,
  id: string,
): AnnouncementCopy | undefined {
  const value = (dict.guidance.announcements as Record<string, unknown>)[id];
  return isAnnouncementCopy(value) ? value : undefined;
}

export function announcementCopyMap(
  dict: Dictionary,
  catalog: readonly GuidanceAnnouncementDef[],
): Record<string, AnnouncementCopy> {
  const map: Record<string, AnnouncementCopy> = {};
  for (const entry of catalog) {
    const copy = announcementCopyFor(dict, entry.id);
    if (copy) map[entry.id] = copy;
  }
  return map;
}

/** Shipped release notes for Help → What’s new (enabled only). */
export function releaseAnnouncements(): GuidanceAnnouncementDef[] {
  return ANNOUNCEMENT_CATALOG.filter(
    (entry) => entry.kind === 'ann.release' && entry.enabled,
  );
}

export function announcementById(
  id: string,
): GuidanceAnnouncementDef | undefined {
  return ANNOUNCEMENT_CATALOG.find((entry) => entry.id === id);
}
